import React, {useEffect, useState, useRef} from 'react';
import {
  View,
  Text,
  Image,
  Pressable,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import {ObjectId} from 'bson';
import moment from 'moment';
import {
  BlobProvider,
  Document,
  View as PDFView,
  Page,
  Text as PDFText,
  Image as PDFImage,
  Font,
  pdf,
} from '@react-pdf/renderer';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';

import {IAttachmentService} from '../../Services/Interfaces/IAttachmentService';
import AttachmentService from '../../Services/AttachmentService';
import DynamicPDFSubmission from './DynamicPDFSubmission';
import LoadingSpinner from '../Shared/LoadingSpinner';
import Icon from '../Shared/Icon';
import {
  TemplateVersion,
  Submission,
  SubmissionTraining,
  Template_Type,
} from '../../Models/PowerSyncModels/models';
import {ControlTypes} from '../../Constants/ControlTypes';
import {DynamicAttachment} from '../../Types/ControlTypes';
import {Formats} from '../../Constants/Formats';
import {METADATA_KEYS} from '../../Constants/AppConstants';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import {Images} from '../../Constants/Images';
import Colors from '../../Styles/Shared/Colors';
import {SignatureListItem} from '../DynamicControls/DynamicSignatureList';

export declare type FileUrl = {
  index?: number;
  name: string;
  url: string;
};

type DynamicPDFProps = {
  submissionId: string;
  type: 'submission' | 'submissionTraining';
  bundleId?: string;
  index?: number;
  addUrls?: (urls: FileUrl[]) => void;
};

Font.register({
  family: 'Poppins',
  fonts: [
    {
      src: '/fonts/Poppins-Regular.ttf',
      fontWeight: 'normal',
    },
    {
      src: '/fonts/Poppins-Bold.ttf',
      fontWeight: 'bold',
    },
  ],
});

export const PDFStyles = StyleSheet.create({
  templateType: {
    color: Colors.darkestGreen,
    fontFamily: 'Poppins',
    fontSize: 24,
    fontWeight: 'bold',
  },
  reportNumber: {
    color: Colors.darkestGreen,
    fontFamily: 'Poppins',
    fontSize: 16,
  },
  reportTitle: {
    color: Colors.darkestGreen,
    fontFamily: 'Poppins',
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  reportSubTitle: {
    color: Colors.darkestGreen,
    fontFamily: 'Poppins',
    fontSize: 16,
  },
  title: {
    color: Colors.darkestGreen,
    fontFamily: 'Poppins',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 24,
  },
  subTitle: {
    color: Colors.darkestGreen,
    fontFamily: 'Poppins',
    fontSize: 14,
    fontWeight: 'bold',
  },
  label: {
    color: Colors.darkestGreen,
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: 'bold',
  },
  description: {
    color: Colors.darkestGreen,
    fontFamily: 'Poppins',
    fontSize: 10,
  },
  value: {
    color: Colors.darkestGreen,
    fontFamily: 'Poppins',
    fontSize: 12,
  },
  footerText: {
    color: Colors.darkestGreen,
    fontFamily: 'Poppins',
    fontWeight: 'bold',
    fontSize: 16,
    paddingTop: 5,
  },
});

export type Report = {
  title: string;
  date: string;
  templateVersion: TemplateVersion;
  templateType: Template_Type;
  submission: Submission | SubmissionTraining;
  attachments: DynamicAttachment[];
  signatures: DynamicAttachment[];
  isLinkedReport: boolean;
};

const DynamicPDF = (props: DynamicPDFProps): React.ReactElement => {
  const attachmentService: IAttachmentService = new AttachmentService(() => {});

  const [orgName, setOrgName] = useState('');
  const [orgLogo, setOrgLogo] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');
  const [reports, setReports] = useState<Report[]>([]);
  const [isMounted, setIsMounted] = useState(false);

  const hasAttachments = useRef(false);

  const [blobDocument, setBlobDocument] = useState<Blob | undefined>(undefined);

  const {
    templateVersions,
    templates,
    templateTypes,
    submissions,
    submissionTrainings,
    submissionLinks,
    submissionBundles,
    organisations,
    equipmentVehicleProfiles,
    equipmentHeavyProfiles,
    equipmentOtherProfiles,
    people,
    projects,
    cities,
    provStates,
    jobs,
  } = usePowerSyncData();

  useEffect(() => {
    const getReports = async () => {
      let newReports: Report[] = [];
      if (props.submissionId) {
        let mainReport = await getReport(props.submissionId, props.type, false);
        if (mainReport) {
          newReports.push(mainReport);

          let org = organisations.find(
            o => o.SQLServerId === mainReport!.submission!.partition,
          );
          if (org) {
            if (org.name) setOrgName(org.name);

            if (org.OrgTagPublic) {
              let uriLogo =
                process.env.REACT_APP_ORG_LOGO_REPO +
                org.OrgTagPublic.toLocaleLowerCase() +
                '/logo.png';

              let logoExists =
                await attachmentService.checkIfLogoExists(uriLogo);

              if (logoExists) setOrgLogo(uriLogo);
            }
          }

          // Get Linked Reports
          let linkedReports = submissionLinks.filter(
            x => x.parentSubmissionId === props.submissionId,
          )!;
          linkedReports.forEach(async link => {
            let linkedReport = await getReport(
              link.childSubmissionId,
              props.type,
              true,
            );
            if (linkedReport) newReports.push(linkedReport);
          });
        }
      }
      setReports(newReports);
      setIsMounted(true);
    };

    getReports();
  }, [props.submissionId]);

  useEffect(() => {
    if (isMounted) {
      const getPdfBlob = async () => {
        let blob = await pdf(renderDocument()).toBlob();
        setBlobDocument(blob);
      };

      getPdfBlob();
    }
  }, [isMounted]);

  useEffect(() => {
    addUrls();
  }, [blobDocument]);

  async function getEquipmentName(id: string): Promise<string> {
    let vehicle = equipmentVehicleProfiles.find(x => x._id === id);
    if (vehicle) return vehicle.name + ' ' + vehicle.number;

    let heavyObj = equipmentHeavyProfiles.find(x => x._id === id);
    if (heavyObj) return heavyObj.name + ' ' + heavyObj.number;

    let otherObj = equipmentOtherProfiles.find(x => x._id === id);
    if (otherObj) return otherObj.name + ' ' + otherObj.number;

    return '';
  }

  async function getPersonName(id: string): Promise<string> {
    let person = people.find(x => x._id === id);
    if (person) return person.firstName + ' ' + person.lastName;

    return '';
  }

  async function getReport(
    submissionId: string,
    type: 'submission' | 'submissionTraining',
    isLinkedReport: boolean,
  ): Promise<Report | undefined> {
    let submission: Submission | SubmissionTraining | undefined = undefined;
    if (props.bundleId) {
      let bundle = submissionBundles.find(s => s._id === props.bundleId!);
      if (bundle && bundle.submissions) {
        let submissionInBundle = bundle.submissions.find(
          s => s.submissionId && s.submissionId === submissionId,
        );

        if (submissionInBundle) {
          submission = {
            _id: submissionInBundle.submissionId,
            partition: bundle.partition,
            createDateTimeStamp: submissionInBundle.createDateTimeStamp,
            createdBy: submissionInBundle.createdBy,
            updateDateTimeStamp: submissionInBundle.updateDateTimeStamp,
            updatedBy: submissionInBundle.updatedBy,
            SQLServerId: submissionInBundle.submissionSQLServerId,
            templateVersion: submissionInBundle.templateVersion,
            templateType: submissionInBundle.templateType,
            metadataJSON: submissionInBundle.metadataJSON,
            answers: submissionInBundle.answers,
          } as Submission;
        }
      }
    } else if (type === 'submission') {
      submission = submissions.find(x => x._id === submissionId);
    } else {
      submission = submissionTrainings.find(x => x._id === submissionId);
    }

    if (submission) {
      let templateVersion = templateVersions.find(
        x => x._id === submission!.templateVersion!._id!,
      )!;
      let template = templates.find(
        x => x._id === templateVersion!.templateId,
      )!;

      let templateType = templateTypes.find(
        x => x._id === template!.templateType!._id,
      )!;

      let title = templateVersion.name ?? '';
      if (submission.metadataJSON) {
        let meta = JSON.parse(submission.metadataJSON);
        if (meta[METADATA_KEYS.TITLE]) title = meta[METADATA_KEYS.TITLE];

        if (meta[METADATA_KEYS.EQUIPMENTID]) {
          let equipmentName = await getEquipmentName(
            meta[METADATA_KEYS.EQUIPMENTID],
          );

          if (equipmentName) title += ': ' + equipmentName;
        } else if (meta[METADATA_KEYS.WORKERID]) {
          let personName = await getPersonName(meta[METADATA_KEYS.WORKERID]);

          if (personName) title += ': ' + personName;
        }

        if (meta[METADATA_KEYS.PROJECT_NAME])
          title += ' at ' + meta[METADATA_KEYS.PROJECT_NAME];
      }
      if (!isLinkedReport) setDocumentTitle(title);

      let date = '';
      let submissionDate =
        submission.updateDateTimeStamp ?? submission.createDateTimeStamp;
      if (submissionDate)
        date = moment(submissionDate).format(Formats.FRONTEND_DATE);

      // Get Attachments & Signatures
      let attachments: DynamicAttachment[] = [];
      let signatures: DynamicAttachment[] = [];
      for (let i = 0; i < templateVersion.pages.length; i++) {
        let page = templateVersion.pages[i];
        if (page.controls)
          for (let j = 0; j < page.controls.length; j++) {
            let control = page.controls[j];
            if (control.controlTypeId === ControlTypes.PHOTOS_AND_ATTACHMENTS) {
              const submissionAnswer = submission.answers.find(
                x => x.controlId == control.controlId,
              );

              let answer = control.value;
              if (submissionAnswer && submissionAnswer.answer)
                answer = submissionAnswer.answer;

              let attachmentsList: DynamicAttachment[] = [];
              try {
                if (answer) {
                  attachmentsList = JSON.parse(answer) as DynamicAttachment[];
                  attachmentsList = attachmentsList.filter(x => x.blobUri);
                }
              } catch (err) {
                console.log('Error: Bad control value serialization');
              }

              for (let i = 0; i < attachmentsList.length; i++) {
                try {
                  let uri = await attachmentService.getSasUri(
                    attachmentsList[i].blobUri!,
                  );
                  attachmentsList[i].uri = uri;
                } catch (err) {
                  console.log('Error: Failed getting sasuri');
                }
              }
              attachments.push(...attachmentsList);
            } else if (control.controlTypeId === ControlTypes.SIGNATURE_LIST) {
              const submissionAnswer = submission.answers.find(
                x => x.controlId == control.controlId,
              );

              let answer = control.value;
              if (submissionAnswer && submissionAnswer.answer)
                answer = submissionAnswer.answer;

              let signaturesList: DynamicAttachment[] = [];
              try {
                let items = JSON.parse(answer) as SignatureListItem[];
                if (items) {
                  for (let i = 0; i < items.length; i++) {
                    if (items[i].attachment?.blobUri) {
                      let uri = await attachmentService.getSasUri(
                        items[i].attachment!.blobUri!,
                      );
                      items[i].attachment!.uri = uri;

                      if (items[i].attachment)
                        signaturesList.push(items[i].attachment!);
                    }
                  }
                }
              } catch (err) {
                console.log('Error: Bad control value serialization');
              }

              signatures.push(...signaturesList);
            } else if (control.controlTypeId === ControlTypes.SIGNATURE) {
              const submissionAnswer = submission.answers.find(
                x => x.controlId == control.controlId,
              );

              let answer = control.value;
              if (submissionAnswer && submissionAnswer.answer)
                answer = submissionAnswer.answer;

              try {
                let signatureAttachment = JSON.parse(
                  answer,
                ) as DynamicAttachment;
                if (signatureAttachment) {
                  if (signatureAttachment?.blobUri) {
                    let uri = await attachmentService.getSasUri(
                      signatureAttachment.blobUri,
                    );
                    signatureAttachment.uri = uri;

                    if (signatureAttachment.uri)
                      signatures.push(signatureAttachment);
                  }
                }
              } catch (err) {
                console.log('Error: Bad control value serialization');
              }
            }
          }
      }

      if (attachments.length > 0) hasAttachments.current = true;

      return {
        title: title,
        date: date,
        templateVersion: templateVersion,
        templateType: templateType,
        submission: submission,
        attachments: attachments,
        signatures: signatures,
        isLinkedReport: isLinkedReport,
      };
    }

    return undefined;
  }

  async function addUrls() {
    if (props.addUrls) {
      let urls: FileUrl[] = [];

      if (blobDocument)
        urls.push({
          index: props.index,
          name: documentTitle.replace(/[^\w\s]/g, '') + '.pdf',
          url: URL.createObjectURL(blobDocument),
        });

      for (let i = 0; i < reports.length; i++) {
        if (reports[i].attachments) {
          for (let j = 0; j < reports[i].attachments.length; j++) {
            if (!reports[i].attachments[j].type?.includes('image')) {
              if (
                reports[i].attachments[j] &&
                reports[i].attachments[j].blobUri &&
                reports[i].attachments[j].name
              ) {
                let sasUri = await attachmentService.getSasUri(
                  reports[i].attachments[j].blobUri!,
                );

                urls.push({
                  index: props.index,
                  name: reports[i].attachments[j].name!,
                  url: sasUri,
                });
              }
            }
          }
        }
      }

      props.addUrls(urls);
    }
  }

  async function downloadDocument() {
    if (blobDocument) {
      var element = document.createElement('a');
      element.href = URL.createObjectURL(blobDocument);
      element.download = documentTitle + '.pdf';
      element.click();
    }
  }

  async function downloadAttachment(
    attachment: DynamicAttachment,
  ): Promise<void> {
    if (attachment && attachment.blobUri && attachment.name)
      await attachmentService.downloadAttachment(
        attachment.blobUri,
        attachment.name,
      );
  }

  const renderDocument = (): React.ReactElement => {
    //Apparently children inside Document node doesn't have access to the context of providers so passing data in manually.

    let listData = {
      projects: projects,
      people: people,
      cities: cities,
      jobs: jobs,
      provStates: provStates,
    };

    return (
      <Document title={documentTitle}>
        {reports.map((report, i) => {
          return (
            <DynamicPDFSubmission
              key={i}
              orgName={orgName}
              orgLogo={orgLogo}
              title={report.title}
              date={report.date}
              templateVersion={report.templateVersion}
              templateType={report.templateType}
              submission={report.submission}
              listData={listData}
              linkedReports={
                report.isLinkedReport
                  ? undefined
                  : reports
                      .filter(r => r.isLinkedReport)
                      .map(r => ({title: r.title, date: r.date}))
              }
              signatures={report.signatures}
            />
          );
        })}
        {reports.map((report, i) => {
          if (report.attachments.length == 0) {
            return <></>;
          }

          return (
            <Page size='A4' style={{paddingTop: 22, paddingBottom: 44}}>
              <PDFView
                style={{
                  paddingHorizontal: 24,
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                }}>
                <PDFText style={[PDFStyles.templateType, {marginBottom: 12}]}>
                  Attachments
                </PDFText>
                <PDFView
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    flexDirection: 'row',
                    marginBottom: '20px',
                  }}>
                  {report.attachments.map(x => (
                    <>{renderAttachment(x)}</>
                  ))}
                </PDFView>
              </PDFView>
              <PDFView
                fixed
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 12,
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  paddingHorizontal: 24,
                  paddingBottom: 12,
                }}>
                <PDFText style={PDFStyles.footerText}>Created with</PDFText>
                <PDFImage source={'/img/FIRS.png'} style={{height: 26}} />
              </PDFView>
            </Page>
          );
        })}
      </Document>
    );
  };

  async function getAttachmentUri(
    attachment: DynamicAttachment,
  ): Promise<string> {
    if (attachment.blobThumbnailUri) {
      return (
        (await attachmentService.getSasUri(attachment.blobThumbnailUri!)) ||
        attachment.uri!
      );
    }
    return attachment.uri!;
  }

  const renderAttachment = (
    attachment: DynamicAttachment,
  ): React.ReactElement => {
    const imageStyle = {objectFit: 'contain', height: 150};
    const viewStyle = {width: '30%', margin: '10px 5px'};
    const textStyle = {
      marginBottom: 12,
      width: '100%',
      overflow: 'hidden' as const,
    };
    let uri = getAttachmentUri(attachment);
    return (
      <>
        {attachment.type?.includes('image') && uri ? (
          <>
            <PDFView style={viewStyle}>
              <PDFImage style={imageStyle} source={uri} />
              <PDFText style={[PDFStyles.label, textStyle, { marginBottom: 4}]}>
                {attachment.name}
              </PDFText>
              <PDFText style={[PDFStyles.description, textStyle]}>
                {attachment.description}
              </PDFText>
            </PDFView>
          </>
        ) : null}
      </>
    );
  };

  return (
    <>
      {isMounted ? (
        <>
          <View style={ControlsStyleSheet.attachmentsItem}>
            <Image style={{width: 32, height: 32}} source={Images.PDF} />
            <Text style={ControlsStyleSheet.attachmentsText}>
              {documentTitle}.pdf
            </Text>
            <View style={ControlsStyleSheet.attachmentsActions}>
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.attachmentsAction,
                  pressed && {
                    backgroundColor: Colors.lightTeal,
                  },
                ]}
                onPress={downloadDocument}>
                <Icon icon={'download'} color={Colors.green} size={20} />
              </Pressable>
            </View>
          </View>
          {hasAttachments.current && (
            <>
              {reports.map((report, i) => {
                return (
                  <>
                    {report.attachments.map((attachment, j) => {
                      if (attachment.type?.includes('image')) return <></>;
                      return (
                        <View
                          key={i.toString() + '-' + j.toString()}
                          style={ControlsStyleSheet.attachmentsItem}>
                          {attachment.type?.includes('pdf') ? (
                            <Image
                              style={{width: 32, height: 32}}
                              source={Images.PDF}
                            />
                          ) : (
                            <Icon
                              icon={'file'}
                              color={Colors.green}
                              size={20}
                            />
                          )}
                          <Text style={ControlsStyleSheet.attachmentsText}>
                            {attachment.name}
                          </Text>
                          <View style={ControlsStyleSheet.attachmentsActions}>
                            <Pressable
                              style={({pressed}) => [
                                ControlsStyleSheet.attachmentsAction,
                                pressed && {
                                  backgroundColor: Colors.lightTeal,
                                },
                              ]}
                              onPress={() => downloadAttachment(attachment)}>
                              <Icon
                                icon={'download'}
                                color={Colors.green}
                                size={20}
                              />
                            </Pressable>
                          </View>
                        </View>
                      );
                    })}
                  </>
                );
              })}
            </>
          )}
        </>
      ) : (
        <LoadingSpinner message='Generating Documents...' visible={true} />
      )}
    </>
  );
};

export default DynamicPDF;
